import { invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';

import { FormVariant } from '../enums/variant.enum';

import { MaterialFormTab } from './enums/tab.enum';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { MaterialForm_Material_Fragment } from './fragments/material.fragment';
import { useMaterialSubmit } from './hooks/useSubmit.hook';
import { useMaterialSteps } from './hooks/useSteps.hook';
import { MaterialSchema } from './schemas/material.schema';
import type { MaterialMutationResult } from './hooks/useSubmit.hook';

export { MaterialFormTab };

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<typeof MaterialForm_Material_Fragment>;
  readonly materialId?: Id;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: MaterialMutationResult) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly MaterialFormTab[];
  readonly title?: string;
}

export const MaterialForm = ({
  askMemoBeforeSubmit = true,
  formVariant,
  initialValues: ppInitialValues,
  materialId,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || materialId),
    'You must provide a materialId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    MaterialForm_Material_Fragment,
    ppInitialValues,
  );

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const { handleSubmit } = useMaterialSubmit({
    formVariant,
    handleSuccess,
    materialId,
  });

  const { steps, wizardChildren } = useMaterialSteps({ stepIdsSubset });

  return (
    <Wizard
      ValidationSchema={MaterialSchema}
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
